// components/estimates/EstimateHeader.js
// Header con titolo, badge stato, pulsanti azioni

import React from 'react';
import { Button } from '../ui/button';
import { 
  ArrowLeft, Save, FileDown, Download, FileSignature, Copy, History, AlertTriangle 
} from 'lucide-react';
import { estimateStatusLabels, estimateStatusColors, getBadgeClass } from '../../lib/utils';

const EstimateHeader = ({
  isNew,
  estimate,
  lead,
  versions,
  hasUnsavedChanges,
  saving,
  downloadingPdf,
  creatingContract,
  loadingPreview,
  formData,
  onBack,
  onSave,
  onDownloadPdf,
  onPreview,
  onCreateContract,
  onShowVersions,
  onCreateVersion
}) => {
  return (
    <div className="bg-white rounded-lg border border-[#DCDCDC] p-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* Left side - Title and badges */}
        <div className="flex items-start gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="mt-0.5"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-semibold text-[#3C3C3C]">
                {isNew ? 'Nuovo Preventivo' : estimate?.estimate_number || ''}
              </h1>
              {!isNew && estimate?.version > 1 && (
                <span className="text-sm text-[#737373]">(v{estimate.version})</span>
              )}
              {!isNew && estimate && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getBadgeClass(estimateStatusColors[estimate.status])}`}>
                  {estimateStatusLabels[estimate.status]}
                </span>
              )}
              {hasUnsavedChanges && (
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Non salvato
                </span>
              )}
            </div>
            {/* Info row */}
            <div className="flex items-center gap-3 mt-1 text-sm text-[#737373]">
              {!isNew && lead && (
                <span className="flex items-center gap-1">
                  <span>Cliente:</span>
                  <span className="font-medium text-[#3C3C3C]">{lead.first_name} {lead.last_name}</span>
                </span>
              )}
              {!isNew && versions.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onShowVersions}
                  className="text-xs text-[#737373] hover:text-[#FF7819] h-5 px-2 py-0"
                  data-testid="show-versions-btn"
                >
                  <History className="w-3 h-3 mr-1" />
                  {versions.length} versioni
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Right side - Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && formData.items.length > 0 && (
            <>
              {/* Preview Button */}
              <div className="relative">
                <select
                  onChange={(e) => {
                    if (e.target.value) onPreview(e.target.value);
                    e.target.value = '';
                  }}
                  disabled={loadingPreview}
                  className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-1.5 pr-8 text-sm cursor-pointer hover:border-[#FF7819] focus:outline-none focus:ring-2 focus:ring-[#FF7819]/20"
                  data-testid="preview-select"
                >
                  <option value="">👁️ Anteprima</option>
                  <option value="customer">PDF Cliente</option>
                  <option value="detailed">PDF Completo</option>
                </select>
              </div>
              
              {/* PDF Downloads */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onDownloadPdf('client')}
                disabled={downloadingPdf === 'client'}
                data-testid="download-pdf-client-btn"
              >
                <FileDown className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{downloadingPdf === 'client' ? '...' : 'PDF Cliente'}</span>
                <span className="sm:hidden">PDF</span>
              </Button>
              <Button 
                variant="outline"
                size="sm" 
                onClick={() => onDownloadPdf('full')}
                disabled={downloadingPdf === 'full'}
                data-testid="download-pdf-full-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{downloadingPdf === 'full' ? '...' : 'PDF Completo'}</span>
                <span className="sm:hidden">Full</span>
              </Button>
              
              <Button 
                variant="outline"
                size="sm"
                onClick={onCreateContract}
                disabled={creatingContract}
                className="border-[#FF7819] text-[#FF7819] hover:bg-[#FFF0E5]"
                data-testid="create-contract-btn"
              >
                <FileSignature className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{creatingContract ? '...' : 'Contratto'}</span>
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={onCreateVersion}
                disabled={hasUnsavedChanges}
                className="border-[#3C3C3C] text-[#3C3C3C] hover:bg-[#F2F2F2]"
                data-testid="create-version-btn"
                title={hasUnsavedChanges ? 'Salva prima le modifiche' : 'Crea nuova versione'}
              >
                <Copy className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Versione</span>
              </Button>
            </>
          )}
          <Button 
            className="btn-primary" 
            size="sm"
            onClick={onSave}
            disabled={saving}
            data-testid="save-estimate-btn"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? '...' : 'Salva'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EstimateHeader;
